import React from 'react';
import {Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import PlaceholderGraph from 'rsuite/cjs/Placeholder/PlaceholderGraph';
import {t} from '@lingui/core/macro';
import cn from 'classnames';

import Box from 'shared/ui/Box/Box';
import { isTranslate } from 'shared/lib/lang';

import {PALETTE} from 'shared/config/model/palette';

import styles from './ResponseTimeChart.module.scss';

export interface IResponseTimePoint {
    name: string,
    value: number,
}

interface IResponseTimeChartProps {
    height: number,
    data?: IResponseTimePoint[],
    isLoading?: boolean,
    hasError?: boolean,
    paused?: boolean,
    disableAnimation?: boolean,
}

const ResponseTimeChart: React.FC<IResponseTimeChartProps> = ({
    height,
    data,
    isLoading,
    hasError,
    paused,
    disableAnimation,
}) => {
    const isPaused = Boolean(paused);

    if (isLoading) {
        return <PlaceholderGraph active height={height} />;
    }

    if (hasError) {
        return (
            <Box className={cn(
                styles.container,
                isTranslate() && styles.enContainer,
                isPaused && styles.pausedBackground,
            )}>
                <div className={styles.error}>
                    {t`Ошибка загрузки данных`}
                </div>
            </Box>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Box className={cn(
                styles.container,
                isTranslate() && styles.enContainer,
                isPaused && styles.pausedBackground,
            )}>
                <div className={styles.empty}>
                    {t`Нет данных для отображения`}
                </div>
            </Box>
        );
    }

    return (
        <Box className={cn(
            styles.container,
            isTranslate() && styles.enContainer,
            isPaused && styles.pausedBackground,
        )}>
            <h3 className={styles.title}>
                {t`Время ответа за последние 24 ч.`}
            </h3>
            <div className={styles.chartWrapper} style={{ height: `${height}px` }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={data}
                        margin={{
                            top: 10,
                            right: 0,
                            left: 0,
                            bottom: 0,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                        <XAxis
                            dataKey="name"
                            tick={{ fontSize: 12 }}
                        />
                        <Tooltip />
                        <YAxis
                            orientation="right"
                            width={40}
                            tick={{ fontSize: 12 }}
                        />
                        <Area
                            type="monotone"
                            name={t`Миллисекунды`}
                            dataKey="value"
                            stroke={
                                isTranslate()
                                    ? PALETTE.accentEn
                                    : isPaused ? PALETTE.neutral : PALETTE.accent
                            }
                            fill="none"
                            strokeWidth={2}
                            isAnimationActive={!disableAnimation}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </Box>
    );
};

export default ResponseTimeChart;